import React from "react";
import {
	Box,
	Button,
	ButtonGroup,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Close } from "@mui/icons-material";
import "react-csv-importer/dist/index.css";
import "./ImportForm.css";
import { ProagricaImportForm, AgrianImportForm, SingleImportForm, CSVImportForm, BulkImportForm } from "../Methods";
import ImportTableWithMap from "../ImportTable/ImportTableWithMap";
import { PortalTooltip, LoadingComponent } from "../../helpers";
import { ImportOption, ImportOptionChoices } from "../../../utilities/types";
import type { Branch, Company, Contact, Deal, Grower, Region } from "../../../models";
import { JobSubmissionFormProps, ImportFormViewModel } from "./ImportForm.types";
import { useImportFormViewModel } from "./ImportForm.vm";

function DataImportSelection({
	importOptions,
	dataImportOption,
	onOptionSelect,
	isDev,
	isSuperUser,
	viewModel,
}: {
	importOptions: ImportOption[];
	dataImportOption: ImportOption | null;
	onOptionSelect: (option: ImportOption) => void;
	isDev?: boolean;
	isSuperUser?: boolean;
	viewModel: ImportFormViewModel;
}) {
	const handleOptionClick = (option: ImportOption) => {
		// Modal-based import types
		if (option.id === ImportOptionChoices.BulkUpload.id) {
			viewModel.setGeoDataModalOpen(true);
			viewModel.setGeoDataModalUsed(true);
			onOptionSelect(option);
		} else if (option.id === ImportOptionChoices.Proagrica.id) {
			viewModel.setProagricaModalOpen(true);
			viewModel.setProagricaModalUsed(true);
			onOptionSelect(option);
		} else if (option.id === ImportOptionChoices.Agrian.id) {
			viewModel.setAgrianModalOpen(true);
			viewModel.setAgrianModalUsed(true);
			onOptionSelect(option);
		} else {
			// Inline import types (SingleUpload, BulkUpload/CSV)
			onOptionSelect(option);
		}
	};

	// Check if this option's modal has been used (opened at least once)
	const isModalUsed = (option: ImportOption) => {
		if (option.id === ImportOptionChoices.BulkUpload.id) return viewModel.geoDataModalUsed;
		if (option.id === ImportOptionChoices.Proagrica.id) return viewModel.proagricaModalUsed;
		if (option.id === ImportOptionChoices.Agrian.id) return viewModel.agrianModalUsed;
		return false;
	};

	return (
		<Box className="import-selection-wrapper">
			<ButtonGroup>
				{importOptions.map((option, index) => {
					const button = (
						<Button
							variant={dataImportOption === option ? "contained" : "outlined"}
							onClick={() => handleOptionClick(option)}
						>
							{option.icon}
							<span className="hide-on-mobile-screen">{isDev ? option.superUserLabel : option.label}</span>
							<span className="show-on-mobile-screen">
								{isSuperUser ? option.superUserShortLabel : option.shortLabel}
							</span>
						</Button>
					);

					if (isModalUsed(option)) {
						return (
							<PortalTooltip key={index} title="Click to pick up where you left off">
								{button}
							</PortalTooltip>
						);
					}

					return <React.Fragment key={index}>{button}</React.Fragment>;
				})}
			</ButtonGroup>
		</Box>
	);
}

function DataImportView({
	dataImportOption,
	growers,
	deals,
	selectedCompany,
	branches,
	regions,
	lastCropOptions,
	nextCropOptions,
	salespersons,
	user,
}: {
	dataImportOption: ImportOption | null;
	growers: Grower[];
	deals: Deal[];
	selectedCompany: Company | null;
	branches: Branch[];
	regions?: Region[];
	lastCropOptions: string[];
	nextCropOptions: string[];
	salespersons: Contact[];
	user?: Contact;
}) {
	if (!dataImportOption) return <></>;

	// Common props for inline import forms
	const importFormProps = {
		selectedDeals: [] as Deal[],
		selectedBranches: [] as Branch[],
		selectedCompany,
		deals,
		growers,
		user,
		salespersons,
		branches,
		regions,
		lastCropOptions,
		nextCropOptions,
	};

	// Only render inline forms here - modal forms are rendered in Dialog components
	switch (dataImportOption.id) {
		case ImportOptionChoices.SingleUpload.id:
			return (
				<SingleImportForm
					growers={growers}
					allDeals={deals}
					company={selectedCompany}
					branches={branches}
					lastCropOptions={lastCropOptions}
					nextCropOptions={nextCropOptions}
					salespersons={salespersons}
				/>
			);
		case ImportOptionChoices.CSVUpload.id:
			return <CSVImportForm {...importFormProps} />;
		// Modal-based forms are rendered in Dialog components, not here
		case ImportOptionChoices.BulkUpload.id:
		case ImportOptionChoices.Proagrica.id:
		case ImportOptionChoices.Agrian.id:
			return null;
		default:
			return null;
	}
}

export default function JobSubmissionForm(props: JobSubmissionFormProps) {
	const vm = useImportFormViewModel(props);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	// Common props for modal import forms
	const importFormProps = {
		selectedDeals: [] as Deal[],
		selectedBranches: [] as Branch[],
		selectedCompany: vm.selectedCompany,
		deals: vm.deals,
		growers: vm.growers,
		user: vm.user,
		salespersons: vm.salespersons,
		branches: vm.branches,
		regions: vm.regions,
		lastCropOptions: vm.lastCropOptions,
		nextCropOptions: vm.nextCropOptions,
	};

	return (
		<div className="import-form submit-page">
			<Grid container spacing={1} sx={{ maxWidth: "md", margin: "auto", width: "100%" }} className="grid-container">
				{/* Add spacing component here */}
				<Grid size={12} className="spacer"></Grid>

				{vm.loading && (
					<Box className="loading-wrapper">
						<LoadingComponent />
					</Box>
				)}

				{/* Choose Data Import Method */}
				{vm.selectedCompany && vm.importOptions.length > 1 && (
					<>
						<DataImportSelection
							importOptions={vm.importOptions}
							dataImportOption={vm.dataImportOption}
							onOptionSelect={vm.setDataImportOption}
							isDev={vm.user?.isDev}
							isSuperUser={vm.user?.isSuperUser}
							viewModel={vm}
						/>
					</>
				)}
			</Grid>

			{/* Inline forms (SingleUpload, BulkUpload/CSV) */}
			{vm.selectedCompany && vm.dataImportOption && (
				<>
					<DataImportView
						dataImportOption={vm.dataImportOption}
						growers={vm.growers}
						deals={vm.deals}
						selectedCompany={vm.selectedCompany}
						branches={vm.branches}
						regions={vm.regions}
						lastCropOptions={vm.lastCropOptions}
						nextCropOptions={vm.nextCropOptions}
						salespersons={vm.salespersons}
						user={vm.user}
					/>
				</>
			)}

			{/* GeoData Modal */}
			<Dialog
				open={vm.geoDataModalOpen}
				onClose={() => vm.setGeoDataModalOpen(false)}
				maxWidth={false}
				fullScreen={isMobile}
				keepMounted
				PaperProps={{ sx: { maxWidth: "unset" } }}
			>
				<DialogContent sx={{ overflowX: "hidden", p: 0 }}>
					<BulkImportForm
						key={`geodata-${vm.formKey}`}
						{...importFormProps}
						isInModal={true}
						onJobsExtracted={(jobs, stats) => vm.handleJobsExtracted(jobs, ImportOptionChoices.BulkUpload, stats)}
						onClose={() => vm.setGeoDataModalOpen(false)}
					/>
				</DialogContent>
			</Dialog>

			{/* Proagrica Modal */}
			<Dialog
				open={vm.proagricaModalOpen}
				onClose={() => vm.setProagricaModalOpen(false)}
				maxWidth={false}
				fullScreen={isMobile}
				keepMounted
				PaperProps={{ sx: { maxWidth: 900, width: "100%" } }}
			>
				<DialogTitle sx={{ display: "flex", justifyContent: "flex-end" }}>
					<IconButton onClick={() => vm.setProagricaModalOpen(false)} size="small">
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ overflowX: "hidden" }}>
					<ProagricaImportForm
						key={`proagrica-${vm.formKey}`}
						{...importFormProps}
						isInModal={true}
						onJobsExtracted={(jobs, stats) => vm.handleJobsExtracted(jobs, ImportOptionChoices.Proagrica, stats)}
						onClose={() => vm.setProagricaModalOpen(false)}
					/>
				</DialogContent>
			</Dialog>

			{/* Agrian Modal */}
			<Dialog
				open={vm.agrianModalOpen}
				onClose={() => vm.setAgrianModalOpen(false)}
				maxWidth={false}
				fullScreen={isMobile}
				keepMounted
				PaperProps={{ sx: { maxWidth: 900, width: "100%" } }}
			>
				<DialogTitle sx={{ display: "flex", justifyContent: "flex-end" }}>
					<IconButton onClick={() => vm.setAgrianModalOpen(false)} size="small">
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ overflowX: "hidden" }}>
					<AgrianImportForm
						key={`agrian-${vm.formKey}`}
						{...importFormProps}
						isInModal={true}
						onJobsExtracted={(jobs, stats) => vm.handleJobsExtracted(jobs, ImportOptionChoices.Agrian, stats)}
						onClose={() => vm.setAgrianModalOpen(false)}
					/>
				</DialogContent>
			</Dialog>

			{/* ImportTable - shown when jobs extracted from modal */}
			{vm.extractedJobs.length > 0 && (
				<Box sx={{ width: "100%" }}>
					<ImportTableWithMap
						jobs={vm.extractedJobs}
						updateJobs={(jobs) => vm.setExtractedJobs(jobs)}
						deals={vm.deals}
						allDeals={vm.deals}
						setLoading={(task: string | boolean) => vm.setLoading(!!task)}
						allOptions={{ Boundary: [], Zones: [], Centroids: [], Cores: [], Unknown: [] }}
						resetForm={() => {
							vm.handleResetAfterSubmit();
						}}
						user={vm.userContext}
						allowMap={true}
						featureStats={vm.featureStats}
						integration={
							vm.activeImportType?.id === ImportOptionChoices.Proagrica.id
								? "Proagrica"
								: vm.activeImportType?.id === ImportOptionChoices.Agrian.id
									? "Agrian"
									: undefined
						}
						selectedCompany={vm.selectedCompany ?? undefined}
						salespersons={vm.salespersons ?? []}
						branches={vm.branches ?? []}
						lastCropOptions={vm.lastCropOptions}
						nextCropOptions={vm.nextCropOptions}
					/>
				</Box>
			)}
		</div>
	);
}
